import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../containers/ApiContainer";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatDistance } from "date-fns";
import { AutocompleteAircraft } from "../Settings/HangarSettings/AutocompleteAircraft";
import { QuarantineService } from "../../services/QuarantineService";
import { Aircraft } from "../../types";

type PendingTenantRow = {
  id: string;
  fbo_id: string;
  tail_number: string;
  operator_name: string | null;
  aircraft_type_hint: string | null;
  source: string;
  created_at: string;
  resolved_at: string | null;
  notes: string | null;
};

type PendingTenant = {
  id: string;
  fbo_id: string;
  tail_number: string;
  operator_name: string;
  aircraft_type_hint: string;
  source: string;
  created_at: string;
};

export const QuarantineView: React.FC = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const { airplxIdentity } = useIdentity();
  const snackbar = useSnackbar();
  const [pendingTenants, setPendingTenants] = React.useState<PendingTenant[]>(
    []
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [processingId, setProcessingId] = React.useState<string | null>(null);

  const fetchPendingTenants = React.useCallback(async () => {
    if (!activeFBO?.id) return;

    setLoading(true);
    try {
      const result = await postgrest
        .from("flightbridge_pending_tenants")
        .select(
          "id, fbo_id, tail_number, operator_name, aircraft_type_hint, source, created_at, resolved_at, notes"
        )
        .eq("fbo_id", activeFBO.id)
        .is("resolved_at", null)
        .order("created_at", { ascending: false });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      if (result.data) {
        const mapped = result.data.map((row: PendingTenantRow) => ({
          id: row.id,
          fbo_id: row.fbo_id,
          tail_number: row.tail_number,
          operator_name: row.operator_name || t("screens.quarantine.fallbacks.unknownOperator"),
          aircraft_type_hint: row.aircraft_type_hint || t("screens.quarantine.fallbacks.unknownType"),
          source: row.source,
          created_at: row.created_at,
        }));
        setPendingTenants(mapped);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError"));
    } finally {
      setLoading(false);
    }
  }, [postgrest, activeFBO?.id, t]);

  React.useEffect(() => {
    fetchPendingTenants();
  }, [fetchPendingTenants]);

  const handleMapAircraft = async (
    tenant: PendingTenant,
    aircraft: Aircraft
  ) => {
    if (!airplxIdentity?.userid) {
      snackbar.notify({ text: t("screens.quarantine.messages.userNotAuthenticated"), severity: "error" });
      return;
    }

    setProcessingId(tenant.id);

    try {
      const serviceTenant = {
        id: tenant.id,
        fbo_id: tenant.fbo_id,
        tail_number: tenant.tail_number,
        operator_name: tenant.operator_name,
        aircraft_type_hint: tenant.aircraft_type_hint,
      };

      const result = await QuarantineService.resolveForFboUser(
        postgrest,
        serviceTenant,
        aircraft,
        airplxIdentity.userid
      );

      snackbar.notify({ text: result.message, severity: "success" });
      await fetchPendingTenants();
    } catch (err) {
      snackbar.notify({
        text: err instanceof Error ? err.message : t("screens.quarantine.messages.mappingFailed"),
        severity: "error",
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (!activeFBO) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t("screens.quarantine.alerts.noActiveFBO")}</Alert>
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={3} sx={{ p: 3, height: "100%" }}>
      {/* Header */}
      <Box>
        <Typography variant="h5" fontWeight="bold">
          {t("screens.quarantine.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("screens.quarantine.description")}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {pendingTenants.length === 0 && !loading ? (
        <Alert severity="success">
          {t("screens.quarantine.alerts.noPendingAircraft")}
        </Alert>
      ) : (
        <Paper
          elevation={1}
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("screens.quarantine.toolbar.pendingCount", { count: pendingTenants.length })}
            </Typography>
            <Button
              size="small"
              startIcon={
                loading ? <CircularProgress size={16} /> : <RefreshIcon />
              }
              onClick={fetchPendingTenants}
              disabled={loading}
            >
              {t("screens.quarantine.toolbar.refresh")}
            </Button>
          </Box>

          {/* Table */}
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t("screens.quarantine.table.headers.tailNumber")}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t("screens.quarantine.table.headers.typeHint")}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t("screens.quarantine.table.headers.operator")}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                      minWidth: 280,
                    }}
                  >
                    {t("screens.quarantine.table.headers.mapToAircraft")}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t("screens.quarantine.table.headers.created")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : pendingTenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("screens.quarantine.table.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingTenants.map((tenant) => {
                    const isProcessing = processingId === tenant.id;
                    return (
                      <TableRow key={tenant.id} hover>
                        <TableCell>
                          <Link
                            href={`https://www.flightradar24.com/data/aircraft/${tenant.tail_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {tenant.tail_number}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", color: "text.secondary" }}
                          >
                            {tenant.aircraft_type_hint}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {tenant.operator_name}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {isProcessing ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="text.secondary">
                                {t("screens.quarantine.table.processing")}
                              </Typography>
                            </Box>
                          ) : (
                            <AutocompleteAircraft
                              label={t("screens.quarantine.table.selectAircraft")}
                              onSelect={(aircraft) =>
                                handleMapAircraft(tenant, aircraft)
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDistance(
                              new Date(tenant.created_at),
                              new Date(),
                              { addSuffix: true }
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
};
